(() => {
  const watched = new WeakSet();
  const setImageState = (image, state) => {
    if (image.dataset.nemoImageState !== state) image.dataset.nemoImageState = state;
  };
  const selector = 'main img, #MainContent img, header img, footer img, [role="dialog"] img';
  const settle = (image) => {
    setImageState(image, image.naturalWidth > 0 ? 'ready' : 'error');
  };
  const track = (image) => {
    if (!(image instanceof HTMLImageElement) || !image.matches(selector)) return;
    if (!image.hasAttribute('src') && !image.hasAttribute('srcset')) return;
    if (!watched.has(image)) {
      watched.add(image);
      image.addEventListener('load', () => settle(image));
      image.addEventListener('error', () => setImageState(image, 'error'));
    }
    if (image.complete) settle(image);
    else setImageState(image, 'pending');
  };
  const scan = (root) => {
    if (!(root instanceof Element) && root !== document) return;
    if (root instanceof HTMLImageElement) track(root);
    root.querySelectorAll(selector).forEach(track);
  };
  scan(document);
  const galleries = new WeakSet();
  const initialiseGallery = (host) => {
    if (galleries.has(host)) return;
    galleries.add(host);
    let frame = 0;
    let finished = false;
    let timeout;
    const observer = new MutationObserver(() => schedule());
    const finish = () => {
      if (finished) return;
      finished = true;
      host.dataset.nemoGalleryLoading = 'ready';
      observer.disconnect();
      clearTimeout(timeout);
      cancelAnimationFrame(frame);
      host.removeEventListener('load', schedule, true);
      host.removeEventListener('error', schedule, true);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('load', schedule);
    };
    const visible = (image) => {
      if (image.closest('[hidden], [aria-hidden="true"]')) return false;
      if (!image.getClientRects().length) return false;
      for (let node = image; node && node !== host.parentElement; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity || 1) === 0) return false;
      }
      return true;
    };
    const check = () => {
      frame = 0;
      if (!host.isConnected) return finish();
      const images = [...host.querySelectorAll('.product-media img, .nemo-gallery__media img, .rio-media-gallery img')];
      const bounds = host.getBoundingClientRect();
      const candidates = images.filter(visible).filter(image => {
        if (!image.closest('.rio-media-gallery')) return true;
        const rect = image.getBoundingClientRect();
        const overlap = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);
        return overlap >= Math.min(120, bounds.width * .35) && rect.width >= bounds.width * .35;
      });
      candidates.sort((a, b) => {
        const first = a.getBoundingClientRect();
        const second = b.getBoundingClientRect();
        return second.width * second.height - first.width * first.height;
      });
      const image = candidates[0];
      if (image?.complete) finish();
    };
    function schedule() {
      if (!finished && !frame) frame = requestAnimationFrame(check);
    }
    host.addEventListener('load', schedule, true);
    host.addEventListener('error', schedule, true);
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    observer.observe(host, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'src', 'srcset'] });
    timeout = setTimeout(finish, 15000);
    check();
  };
  const scanGalleries = () => document.querySelectorAll('[data-nemo-gallery-loading="pending"], .product-information__media:has(.rio-media-gallery)').forEach(host => {
    if (!galleries.has(host)) {
      host.dataset.nemoGalleryLoading = 'pending';
      initialiseGallery(host);
    }
  });
  scanGalleries();
  const mobileGalleries = new WeakSet();
  const prepareMobileGallery = (host) => {
    if (mobileGalleries.has(host) || !matchMedia('(max-width: 749px)').matches) return;
    mobileGalleries.add(host);
    let queued = 0;
    const prepared = new WeakMap();
    const warm = () => {
      queued = 0;
      if (!host.isConnected || !matchMedia('(max-width: 749px)').matches) return;
      if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const slides = [...host.querySelectorAll('.pwzrswiper-slide, slideshow-slide, .nemo-gallery__media')];
      if (!slides.length) return;
      let index = slides.findIndex(slide => slide.matches('.pwzrswiper-slide-active, [aria-hidden="false"], .is-active'));
      if (index < 0) index = 0;
      const current = slides[index].querySelector('img');
      if (!current?.complete || !current.naturalWidth) return;
      for (const offset of [1, -1, 2]) {
        const slide = slides[(index + offset + slides.length) % slides.length];
        const image = slide?.querySelector('img');
        if (!image || image === current || !image.getAttribute('src')) continue;
        const source = (image.getAttribute('srcset') || '') + image.getAttribute('src');
        if (prepared.get(image) === source) continue;
        prepared.set(image, source);
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        if (typeof image.decode === 'function') image.decode().catch(() => {});
      }
    };
    const schedule = () => {
      if (!queued) queued = requestAnimationFrame(warm);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(host, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-hidden', 'hidden', 'src', 'srcset']});
    host.addEventListener('load', schedule, true);
    host.addEventListener('pointerdown', schedule, {passive: true});
    schedule();
  };
  const scanMobileGalleries = () => document.querySelectorAll('.product-information__media, [data-nemo-gallery]').forEach(prepareMobileGallery);
  scanMobileGalleries();
  let galleryScanFrame = 0;
  const queueGalleryScan = () => {
    if (galleryScanFrame) return;
    galleryScanFrame = requestAnimationFrame(() => {
      galleryScanFrame = 0;
      scanGalleries();
      scanMobileGalleries();
    });
  };
  new MutationObserver((records) => {
    let addedElements = false;
    for (const record of records) {
      if (record.type === 'attributes') {
        if (record.target instanceof HTMLImageElement) track(record.target);
        else record.target.closest('picture')?.querySelectorAll('img').forEach(track);
      } else record.addedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        scan(node);
        addedElements = true;
      });
    }
    if (addedElements) queueGalleryScan();
  }).observe(document.documentElement, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['src', 'srcset', 'sizes']
  });
})();
